import React, { useState } from 'react';
import { Dialog, DialogContent } from './ui/dialog';
import { Button } from './ui/button';
import { ZoomInIcon, ZoomOutIcon, DownloadIcon, FileTextIcon, XIcon, RotateCcwIcon } from 'lucide-react';

// Belge Önizleme Modalı — modal içinde zoom in/out + indirme.
// Yeni sekmede açmayı değiştirir. Resim + PDF destekler.
const BelgeOnizlemeModal = ({ file, onClose }) => {
  const [zoom, setZoom] = useState(1);
  if (!file?.url) return null;

  const isImage = (file.type || '').startsWith('image/')
    || /\.(jpe?g|png|gif|webp|heic|heif)$/i.test(file.name || '');
  const isPdf = (file.type || '').includes('pdf')
    || /\.pdf$/i.test(file.name || '');

  const handleZoomIn = () => setZoom(z => Math.min(z + 0.25, 4));
  const handleZoomOut = () => setZoom(z => Math.max(z - 0.25, 0.25));
  const handleReset = () => setZoom(1);

  const download = () => {
    // Create an <a> with download attribute; falls back to opening the file
    // in-place if the browser blocks the download hint.
    const a = document.createElement('a');
    a.href = file.url;
    a.download = file.name || 'belge';
    a.rel = 'noopener noreferrer';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  return (
    <Dialog open={!!file} onOpenChange={(o) => { if (!o) onClose && onClose(); }}>
      <DialogContent
        className="max-w-5xl w-[95vw] h-[90vh] p-0 overflow-hidden flex flex-col"
        data-testid="belge-onizleme-modal"
      >
        {/* Header — filename + action buttons */}
        <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-gray-200 bg-white">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <FileTextIcon className="w-4 h-4 text-gray-500 flex-shrink-0" />
            <span className="text-sm font-medium text-gray-900 truncate" title={file.name}>
              {file.name || 'Belge'}
            </span>
            {file.size != null && (
              <span className="text-xs text-gray-400 flex-shrink-0">
                ({Math.round(file.size / 1024)} KB)
              </span>
            )}
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            {isImage && (
              <>
                <Button
                  type="button" variant="outline" size="sm"
                  onClick={handleZoomOut} disabled={zoom <= 0.25}
                  data-testid="belge-zoom-out"
                  aria-label="Uzaklaştır"
                >
                  <ZoomOutIcon className="w-4 h-4" />
                </Button>
                <span className="text-xs text-gray-600 w-12 text-center font-mono" data-testid="belge-zoom-level">
                  {Math.round(zoom * 100)}%
                </span>
                <Button
                  type="button" variant="outline" size="sm"
                  onClick={handleZoomIn} disabled={zoom >= 4}
                  data-testid="belge-zoom-in"
                  aria-label="Yakınlaştır"
                >
                  <ZoomInIcon className="w-4 h-4" />
                </Button>
                <Button
                  type="button" variant="outline" size="sm"
                  onClick={handleReset} disabled={zoom === 1}
                  data-testid="belge-zoom-reset"
                  aria-label="Sıfırla"
                  title="Zoom'u sıfırla (100%)"
                >
                  <RotateCcwIcon className="w-4 h-4" />
                </Button>
                <div className="w-px h-6 bg-gray-200 mx-1" />
              </>
            )}
            <Button
              type="button" size="sm"
              onClick={download}
              className="bg-teal-600 hover:bg-teal-700"
              data-testid="belge-download-btn"
            >
              <DownloadIcon className="w-4 h-4 mr-1.5" /> İndir
            </Button>
            <Button
              type="button" variant="outline" size="sm"
              onClick={onClose}
              aria-label="Kapat"
              data-testid="belge-close-btn"
            >
              <XIcon className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Preview area */}
        <div className="flex-1 overflow-auto bg-gray-100 flex items-center justify-center p-4">
          {isImage && (
            <img
              src={file.url}
              alt={file.name || 'Belge'}
              className="max-w-full max-h-full transition-transform duration-150"
              style={{ transform: `scale(${zoom})`, transformOrigin: 'center' }}
              data-testid="belge-image-preview"
            />
          )}
          {isPdf && (
            <iframe
              src={file.url}
              title={file.name || 'PDF önizleme'}
              className="w-full h-full bg-white border border-gray-200"
              data-testid="belge-pdf-preview"
            />
          )}
          {!isImage && !isPdf && (
            <div className="text-center text-gray-500 space-y-3">
              <FileTextIcon className="w-16 h-16 mx-auto text-gray-300" />
              <p className="text-sm">Bu dosya türü tarayıcıda önizlenemez.</p>
              <p className="text-xs">İndir butonuyla dosyayı bilgisayarınıza kaydedebilirsiniz.</p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default BelgeOnizlemeModal;
